import { GridReadyEvent } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

import * as yup from 'yup';
import { AgTable } from '../../../../../components/AgTable/AgTable';
import { RendererType } from '../../../../../data-models/field-definition';
import { percentField, usdField } from '../../../../../schemas/common-schema-defs';
import { useSchemaToGrid } from '../../../../../util/schema-utils';
import { useSelectedCurrency } from '../../../../../hooks/useSelectedCurrency';
import { selectedViewTemporalDatePFJ } from '../../../state/PageStateJ';
import { roundTrackerMetricsByDateAtom } from '../../../state/RoundTrackerMetricsStateJ';

const defaultColDef = {
  minWidth: 150,
  flex: 1,
};

export interface ISimplifiedRoundGridProps {
  selectedCompanyId: number;
}
export function SimplifiedRoundGrid(props: ISimplifiedRoundGridProps) {
  const { selectedCompanyId } = props;
  const getColDefs = useSchemaToGrid();
  const date = useAtomValue(selectedViewTemporalDatePFJ);
  const { selectedCurrency } = useSelectedCurrency();
  const { data: metrics } = useAtomValue(roundTrackerMetricsByDateAtom({ date, currency: selectedCurrency }));

  const rowData = useMemo(() => {
    if (metrics) {
      return (metrics ?? []).filter((metric) => metric.companyId === selectedCompanyId);
    }
    return [];
  }, [metrics, selectedCompanyId]);

  const colDefs = useMemo(() => {
    return getColDefs(getSimplifiedRoundGridSchema());
  }, [getColDefs]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    event.api.autoSizeColumns(event.api.getColumns() ?? []);
  }, []);

  return (
    <AgTable
      columnDefs={colDefs}
      groupDefaultExpanded={-1}
      onGridReady={onGridReady}
      rowData={rowData}
      rowGroupPanelShow='never'
      suppressMovableColumns={true}
      suppressRowDrag={true}
      defaultColDef={defaultColDef}
    />
  );
}

function getSimplifiedRoundGridSchema() {
  return yup.object({
    investmentDate: yup.string().label('Round Date').gridMeta({
      formatter: 'dateNumeric',
    }),
    investmentRoundId: yup.string().gridMeta({
      label: 'Stage',
      formatter: 'stage',
      renderer: RendererType.id,
    }),
    round: yup.string().gridMeta({
      label: 'Round',
      key: 'investmentRoundId',
      formatter: 'investmentRound',
      renderer: RendererType.id,
    }),
    raiseAmount: usdField(),
    pricePerShare: usdField(),
    postMoneyValuation: usdField(),
    security: yup.string(),
    gcAmount: usdField().label('Investment Amount'),
    noOfShares: yup.number(),
    ownershipPercentage: percentField().label('Ownership Percentage'),
    leadInvestors: yup.array().of(yup.string()).customMeta({
      renderer: RendererType.multiSelect,
    }),
  });
}
